<script lang="ts">
  import Breadcrumb from '../shared/Breadcrumb.svelte';
  import EnvSwitcher from '../shared/EnvSwitcher.svelte';
  import StatusBadge from '../shared/StatusBadge.svelte';
  import {
    fetchStrategyLifecycle,
    STATE_COLORS,
    type LifecycleData,
    type Strategy,
    type StrategyState,
  } from '../../services/strategy-client';

  let env = $state<'Beta' | 'Prod'>('Beta');
  let advertiserId = $state('A1B2C3D4E5');
  let data = $state<LifecycleData | null>(null);
  let loading = $state(false);
  let queried = $state(false);
  let selectedStrategy = $state<Strategy | null>(null);

  const STATES: StrategyState[] = ['GENERATED', 'SURFACED', 'ADOPTED', 'ENABLED', 'DISABLED'];

  const stateBadgeStatus: Record<StrategyState, 'info' | 'warning' | 'success' | 'error' | 'pending'> = {
    GENERATED: 'info',
    SURFACED: 'pending',
    ADOPTED: 'warning',
    ENABLED: 'success',
    DISABLED: 'error',
  };

  async function search() {
    if (!advertiserId.trim()) return;
    loading = true;
    queried = true;
    selectedStrategy = null;
    try {
      data = await fetchStrategyLifecycle(advertiserId.trim(), env);
    } finally {
      loading = false;
    }
  }

  const totalForBar = $derived(data ? Math.max(data.totalStrategies, 1) : 1);

  function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
</script>

<div class="min-h-screen bg-bg-dark text-text-primary">
  <div class="w-full px-6 py-4">
    <div class="flex items-center justify-between pr-20">
      <Breadcrumb items={[{ label: 'Strategy Lifecycle' }]} />
      <EnvSwitcher bind:value={env} />
    </div>

    <div class="mt-4 mb-6">
      <h1 class="text-2xl font-bold">🔄 Strategy Lifecycle</h1>
      <p class="mt-1 text-sm text-text-secondary">Track strategy state transitions from GENERATED through ENABLED/DISABLED</p>
    </div>

    <!-- Search -->
    <div class="bg-bg-panel border border-border rounded-xl p-5 mb-6">
      <div class="flex items-end gap-4">
        <div class="flex-1 max-w-lg">
          <label for="adv-id" class="block text-xs text-text-secondary mb-1.5">Advertiser ID</label>
          <input
            id="adv-id"
            type="text"
            bind:value={advertiserId}
            placeholder="A1B2C3D4E5"
            onkeydown={(e) => e.key === 'Enter' && search()}
            class="w-full bg-bg-input border border-border rounded-lg px-3 py-2 text-sm text-text-primary font-mono placeholder:text-text-muted focus:outline-none focus:border-accent"
          />
        </div>
        <button
          onclick={search}
          disabled={loading}
          class="px-5 py-2 bg-accent hover:bg-accent-hover text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          {loading ? 'Searching…' : 'Search'}
        </button>
      </div>
    </div>

    {#if loading}
      <div class="h-32 rounded-xl bg-bg-panel animate-skeleton-pulse mb-4"></div>
      <div class="h-64 rounded-xl bg-bg-panel animate-skeleton-pulse"></div>
    {:else if queried && !data}
      <div class="bg-bg-panel border border-border rounded-xl p-12 text-center">
        <p class="text-4xl mb-3">🔍</p>
        <p class="text-text-secondary">No strategies found</p>
      </div>
    {:else if data}
      <!-- Distribution Bar Chart -->
      <div class="bg-bg-panel border border-border rounded-xl p-5 mb-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-sm font-medium">State Distribution</h3>
          <span class="text-xs text-text-muted">{data.totalStrategies} total strategies</span>
        </div>

        <!-- CSS-only bar chart -->
        <div class="flex rounded-lg overflow-hidden h-10 bg-bg-dark/50">
          {#each STATES as state (state)}
            {@const count = data.distribution[state]}
            {#if count > 0}
              <div
                class="flex items-center justify-center text-xs font-semibold text-white transition-all duration-500"
                style="width: {(count / totalForBar) * 100}%; background-color: {STATE_COLORS[state]}"
                title="{state}: {count}"
              >
                {count}
              </div>
            {/if}
          {/each}
        </div>

        <!-- Legend -->
        <div class="flex flex-wrap gap-4 mt-3">
          {#each STATES as state (state)}
            <div class="flex items-center gap-1.5 text-xs">
              <span class="w-3 h-3 rounded-sm" style="background-color: {STATE_COLORS[state]}"></span>
              <span class="text-text-secondary">{state}</span>
              <span class="font-semibold">{data.distribution[state]}</span>
            </div>
          {/each}
        </div>
      </div>

      <!-- Strategy List + Detail -->
      <div class="flex gap-6">
        <div class="flex-1 min-w-0">
          <h3 class="text-sm font-medium text-text-secondary mb-3">Strategies</h3>
          <div class="space-y-2">
            {#each data.strategies as strategy (strategy.strategyId)}
              <button
                onclick={() => selectedStrategy = selectedStrategy?.strategyId === strategy.strategyId ? null : strategy}
                class="w-full text-left bg-bg-panel border rounded-lg p-4 transition-colors hover:border-accent/50 {selectedStrategy?.strategyId === strategy.strategyId ? 'border-accent' : 'border-border'}"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <span class="font-mono text-sm font-semibold">{strategy.strategyId}</span>
                    <StatusBadge status={stateBadgeStatus[strategy.currentState]} label={strategy.currentState} size="sm" />
                  </div>
                  <span class="text-xs text-text-muted">{strategy.strategyType}</span>
                </div>
                <div class="flex items-center gap-4 mt-2 text-xs text-text-muted">
                  <span>Campaign: {strategy.campaignId}</span>
                  <span>Created: {formatDate(strategy.createdAt)}</span>
                  <span>{strategy.transitions.length} transitions</span>
                </div>
              </button>
            {/each}
          </div>
        </div>

        <!-- Transition Timeline -->
        {#if selectedStrategy}
          <div class="w-80 shrink-0 animate-fade-in-up">
            <h3 class="text-sm font-medium text-text-secondary mb-3">
              Timeline — {selectedStrategy.strategyId}
            </h3>
            <div class="bg-bg-panel border border-border rounded-xl p-5">
              <div class="relative">
                {#each selectedStrategy.transitions as t, i (i)}
                  <div class="flex gap-3 {i < selectedStrategy.transitions.length - 1 ? 'pb-6' : ''}">
                    <!-- Dot + Line -->
                    <div class="flex flex-col items-center">
                      <div class="w-3 h-3 rounded-full shrink-0 border-2" style="border-color: {STATE_COLORS[t.to]}; background-color: {STATE_COLORS[t.to]}40"></div>
                      {#if i < selectedStrategy.transitions.length - 1}
                        <div class="w-px flex-1 bg-border-light mt-1"></div>
                      {/if}
                    </div>
                    <!-- Content -->
                    <div class="pb-1 -mt-0.5">
                      <div class="flex items-center gap-2">
                        <span class="text-xs font-semibold" style="color: {STATE_COLORS[t.to]}">{t.to}</span>
                        {#if t.from}
                          <span class="text-[10px] text-text-muted">from {t.from}</span>
                        {/if}
                      </div>
                      <p class="text-[10px] text-text-muted mt-0.5">{formatDate(t.timestamp)}</p>
                      <p class="text-[10px] text-text-secondary">by {t.actor}</p>
                    </div>
                  </div>
                {/each}
              </div>
            </div>
          </div>
        {/if}
      </div>
    {/if}
  </div>
</div>
